import { TrackingEvent } from '@acretrader/acremaps-analytics-events';
import { useCallback, useMemo, useState } from 'react';

import {
  shouldHideSectionLayersKey,
  shouldHideTownshipLayersKey,
} from '@/constants/localStorage';
import useWebStorage from '@/hooks/useWebStorage';
import { useAnalytics } from '@/providers/AnalyticsProvider';
import { unimplementedFunction } from '@/utils/unimplementedFunction';

type StateSpecificState = {
  shouldHideStateLayer: boolean;
  setShouldHideStateLayer: (value: boolean) => void;
  shouldRenderStateLayer: boolean;
};

const emptyStateSpecificState: StateSpecificState = {
  setShouldHideStateLayer: unimplementedFunction,
  shouldHideStateLayer: false,
  shouldRenderStateLayer: true,
};

type CountySpecificState = {
  shouldHideCountyLayer: boolean;
  setShouldHideCountyLayer: (value: boolean) => void;
  shouldRenderCountyLayer: boolean;
};

const emptyCountySpecificState: CountySpecificState = {
  setShouldHideCountyLayer: unimplementedFunction,
  shouldHideCountyLayer: false,
  shouldRenderCountyLayer: true,
};

type TownshipSpecificState = {
  shouldHideTownshipLayer: boolean;
  setShouldHideTownshipLayer: (value: boolean) => void;
  shouldRenderTownshipLayer: boolean;
};

const emptyTownshipSpecificState: TownshipSpecificState = {
  setShouldHideTownshipLayer: unimplementedFunction,
  shouldHideTownshipLayer: false,
  shouldRenderTownshipLayer: true,
};

type SectionSpecificState = {
  shouldHideSectionLayer: boolean;
  setShouldHideSectionLayer: (value: boolean) => void;
  shouldRenderSectionLayer: boolean;
};

const emptySectionSpecificState: SectionSpecificState = {
  setShouldHideSectionLayer: unimplementedFunction,
  shouldHideSectionLayer: false,
  shouldRenderSectionLayer: true,
};

export type GovernmentDistrictLayerState = StateSpecificState &
  CountySpecificState &
  TownshipSpecificState &
  SectionSpecificState;

export const emptyGovernmentDistrictLayerState: GovernmentDistrictLayerState = {
  ...emptyStateSpecificState,
  ...emptyCountySpecificState,
  ...emptyTownshipSpecificState,
  ...emptySectionSpecificState,
};

export const useGovernmentDistrictLayerState =
  (): GovernmentDistrictLayerState => {
    const { track } = useAnalytics();

    const [shouldHideStateLayer, setShouldHideStateLayer] =
      useState<boolean>(false);
    const [shouldHideCountyLayer, setShouldHideCountyLayer] =
      useState<boolean>(false);

    const {
      savedValue: shouldHideTownshipLayer,
      updateValue: setShouldHideTownshipLayer,
    } = useWebStorage<boolean>({ key: shouldHideTownshipLayersKey });
    const {
      savedValue: shouldHideSectionLayer,
      updateValue: setShouldHideSectionLayer,
    } = useWebStorage<boolean>({ key: shouldHideSectionLayersKey });

    const shouldRenderStateLayer = useMemo(
      () => !shouldHideStateLayer,
      [shouldHideStateLayer]
    );

    const shouldRenderCountyLayer = useMemo(
      () => !shouldHideCountyLayer,
      [shouldHideCountyLayer]
    );

    const shouldRenderTownshipLayer = useMemo(
      () => !shouldHideTownshipLayer,
      [shouldHideTownshipLayer]
    );

    const shouldRenderSectionLayer = useMemo(
      () => !shouldHideSectionLayer,
      [shouldHideSectionLayer]
    );

    const trackedHideCountySetter = useCallback(
      (shouldHide: boolean) => {
        setShouldHideCountyLayer(shouldHide);
        track({
          data: {},
          event: shouldHide
            ? TrackingEvent.COUNTY_LAYERS_HIDDEN
            : TrackingEvent.COUNTY_LAYERS_SHOWN,
        });
      },
      [setShouldHideCountyLayer, track]
    );

    const trackedHideSectionSetter = useCallback(
      (shouldHide: boolean) => {
        setShouldHideSectionLayer(shouldHide);
        track({
          data: {},
          event: shouldHide
            ? TrackingEvent.SECTION_LAYERS_HIDDEN
            : TrackingEvent.SECTION_LAYERS_SHOWN,
        });
      },
      [setShouldHideSectionLayer, track]
    );

    const trackedHideStateSetter = useCallback(
      (shouldHide: boolean) => {
        setShouldHideStateLayer(shouldHide);
        track({
          data: {},
          event: shouldHide
            ? TrackingEvent.STATE_LAYERS_HIDDEN
            : TrackingEvent.STATE_LAYERS_SHOWN,
        });
      },
      [setShouldHideStateLayer, track]
    );

    const trackedHideTownshipSetter = useCallback(
      (shouldHide: boolean) => {
        setShouldHideTownshipLayer(shouldHide);
        track({
          data: {},
          event: shouldHide
            ? TrackingEvent.TOWNSHIP_LAYERS_HIDDEN
            : TrackingEvent.TOWNSHIP_LAYERS_SHOWN,
        });
      },
      [setShouldHideTownshipLayer, track]
    );

    return {
      setShouldHideCountyLayer: trackedHideCountySetter,
      setShouldHideSectionLayer: trackedHideSectionSetter,
      setShouldHideStateLayer: trackedHideStateSetter,
      setShouldHideTownshipLayer: trackedHideTownshipSetter,
      shouldHideCountyLayer,
      shouldHideSectionLayer: Boolean(shouldHideSectionLayer),
      shouldHideStateLayer,
      shouldHideTownshipLayer: Boolean(shouldHideTownshipLayer),
      shouldRenderCountyLayer,
      shouldRenderSectionLayer,
      shouldRenderStateLayer,
      shouldRenderTownshipLayer,
    };
  };
